import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicShare, API } from "../lib/api";
import { CheckCircle2, Circle, XCircle, Flame, Info, ArrowUpRight, Heart, Bell, BellOff, FileDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import DigestSubscribeCard from "../components/DigestSubscribeCard";
import { toast } from "sonner";
import axios from "axios";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) out[i] = rawData.charCodeAt(i);
  return out;
}

function fmtDay(s) {
  return new Date(s).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
function fmtTime(s) {
  return new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const statusIcon = (s) => {
  if (s === "taken" || s === "auto_taken") return <CheckCircle2 className="h-4 w-4 text-[color:var(--success)]" />;
  if (s === "skipped") return <XCircle className="h-4 w-4 text-[color:var(--accent-ink)]" />;
  return <Circle className="h-4 w-4 text-[color:var(--text-muted)]" />;
};

const REACTIONS = [
  { emoji: "👍", label: "Cheer" },
  { emoji: "❤️", label: "Love" },
  { emoji: "🔥", label: "Fire" },
  { emoji: "💪", label: "Strong" },
];

export default function SharePage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("thanks") === "1") {
      const emo = params.get("emoji") || "👍";
      toast.success(`Thanks for the cheer! ${emo}`);
      params.delete("thanks"); params.delete("emoji");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem("handy_caregiver_name") || "");
  const [sending, setSending] = useState({});
  const [cgSubscribed, setCgSubscribed] = useState(false);
  const [cgBusy, setCgBusy] = useState(false);

  async function load() {
    try {
      const d = await publicShare.get(token);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Share not found");
    }
  }

  useEffect(() => { load(); }, [token]);

  // Check caregiver push subscription status for this share
  useEffect(() => {
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      try {
        const reg = await navigator.serviceWorker.ready.catch(() => null);
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) { setCgSubscribed(false); return; }
        const { subscribed } = await publicShare.subscriptionStatus(token, sub.endpoint);
        setCgSubscribed(!!subscribed);
      } catch { /* ignore */ }
    })();
  }, [token]);

  async function toggleCaregiverPush(on) {
    setCgBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Push not supported in this browser");
        return;
      }
      if (!name.trim() && on) {
        toast.error("Enter your name first so they know who's getting reminded");
        return;
      }
      // ensure service worker registered
      let reg;
      try { reg = await navigator.serviceWorker.register("/service-worker.js"); }
      catch { reg = await navigator.serviceWorker.ready; }
      if (on) {
        if (Notification.permission !== "granted") {
          const p = await Notification.requestPermission();
          if (p !== "granted") { toast.error("Notifications blocked"); return; }
        }
        // fetch vapid pub key via public endpoint (no client-id required)
        const { data: vk } = await axios.get(`${API}/push/vapid-public-key`);
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vk.public_key),
          });
        }
        const j = sub.toJSON();
        await publicShare.subscribe(token, j.endpoint, j.keys, name.trim());
        localStorage.setItem("handy_caregiver_name", name.trim());
        setCgSubscribed(true);
        toast.success("You'll get reminders for this schedule");
      } else {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const j = sub.toJSON();
          try { await publicShare.unsubscribe(token, j.endpoint, j.keys); } catch { /* ignore */ }
        }
        setCgSubscribed(false);
        toast.success("Reminders stopped for this schedule");
      }
    } catch (e) {
      toast.error("Could not update reminders");
    } finally {
      setCgBusy(false);
    }
  }

  async function react(doseId, emoji) {
    if (!name.trim()) {
      toast.error("Enter your name so they know it's you");
      return;
    }
    localStorage.setItem("handy_caregiver_name", name.trim());
    setSending((s) => ({ ...s, [doseId + emoji]: true }));
    try {
      await publicShare.react(token, doseId, emoji, name.trim());
      toast.success("Cheer sent");
      load();
    } catch {
      toast.error("Could not send");
    } finally {
      setSending((s) => ({ ...s, [doseId + emoji]: false }));
    }
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[color:var(--surface-2)]">
        <div className="card-tactile rounded-2xl p-8 text-center max-w-md">
          <div className="h-12 w-12 rounded-full bg-[color:var(--accent-soft)] flex items-center justify-center mx-auto mb-4">
            <Info className="h-6 w-6 text-[color:var(--accent-ink)]" />
          </div>
          <h1 className="font-display font-bold text-2xl">Share unavailable</h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-2">{err}</p>
          <a href="/" className="inline-flex items-center gap-1 mt-4 text-sm text-[color:var(--primary-ink)] font-semibold hover:underline">
            Open Handy <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-[color:var(--text-muted)]">Loading share…</div>;
  }

  const { prescription: rx, doses, adherence, recent_reactions = [], total_cheers = 0, caregiver_count = 0, next_milestone, events = [] } = data;
  const now = Date.now();
  const upcoming = doses.filter(d => new Date(d.scheduled_at).getTime() > now).slice(0, 5);
  const recent = [...doses.filter(d => new Date(d.scheduled_at).getTime() <= now)].slice(-10).reverse();

  return (
    <div className="min-h-screen bg-[color:var(--bg)] grain">
      <header className="bg-[color:var(--surface)] border-b border-[color:var(--border-subtle)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="VeriHandi" className="h-8 w-8" />
            <span className="font-display font-black tracking-tighter text-xl">Handy</span>
          </a>
          <span className="label-caps">Shared · Read-only</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10 relative z-10 space-y-6">
        <section className="animate-fade-up">
          <span className="label-caps">Course</span>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter" data-testid="share-title">{rx.medicine_name}</h1>
          <p className="mt-2 text-[color:var(--text-muted)]">
            {rx.tapering_schedule && rx.tapering_schedule.length > 1
              ? `${rx.tapering_schedule[0].dose_amount} → ${rx.tapering_schedule[rx.tapering_schedule.length - 1].dose_amount} · tapering`
              : rx.dosage}
            {" · "}{rx.frequency_per_day}x/day · {rx.duration_days} days · started {fmtDay(rx.start_datetime)}
          </p>
          {(() => {
            const bands = rx.tapering_schedule;
            if (!bands || bands.length === 0) return null;
            const today = (doses || []).find(d => {
              const s = new Date(d.scheduled_at);
              const now = new Date();
              return s.toDateString() === now.toDateString();
            });
            if (!today || !today.dose_label) return null;
            return (
              <div
                data-testid="share-today-chip"
                className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full"
                style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}
              >
                <span className="text-[10px] uppercase tracking-wider font-bold">Today</span>
                <span className="text-sm font-semibold">{today.dose_label}</span>
                {today.day_offset && (
                  <span className="text-[11px] opacity-70">· Day {today.day_offset}{today.week_number ? ` · Wk ${today.week_number}` : ""}</span>
                )}
              </div>
            );
          })()}
          {rx.instructions && <p className="text-sm text-[color:var(--text-muted)] mt-1"><Info className="h-3 w-3 inline mr-1" />{rx.instructions}</p>}
        </section>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="share-adherence">
          <div className="card-tactile rounded-xl p-5">
            <span className="label-caps">Adherence</span>
            <div className="font-display font-black text-3xl mt-1">{adherence.adherence_pct}<span className="text-base text-[color:var(--text-muted)]">%</span></div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">{adherence.taken}/{adherence.total_past} doses</div>
          </div>
          <div className="card-tactile rounded-xl p-5">
            <span className="label-caps flex items-center gap-1"><Flame className="h-3 w-3 text-[color:var(--accent-ink)]" /> Current streak</span>
            <div className="font-display font-black text-3xl mt-1">{adherence.current_streak}</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">consecutive taken</div>
          </div>
          <div className="card-tactile rounded-xl p-5">
            <span className="label-caps flex items-center gap-1"><Heart className="h-3 w-3 text-[color:var(--accent-ink)]" /> Total cheers</span>
            <div className="font-display font-black text-3xl mt-1" data-testid="share-total-cheers">{total_cheers}</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">lifetime support</div>
          </div>
          <div className="card-tactile rounded-xl p-5">
            <span className="label-caps">Caregivers</span>
            <div className="font-display font-black text-3xl mt-1" data-testid="share-caregivers-count">{caregiver_count}</div>
            <div className="text-xs text-[color:var(--text-muted)] mt-1">subscribed</div>
          </div>
        </section>

        {next_milestone && adherence.current_streak > 0 && next_milestone.target > adherence.current_streak && (
          <section className="card-tactile rounded-2xl p-5" data-testid="milestone-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">Next milestone</h3>
                <p className="text-sm text-[color:var(--text-muted)]">{adherence.current_streak} / {next_milestone.target} doses · {next_milestone.target - adherence.current_streak} to go</p>
              </div>
              <span className="dose-pill auto-taken"><Flame className="h-3 w-3" /> {next_milestone.target}-streak</span>
            </div>
            <div className="h-3 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#3F7A68] to-[#C46E4E] transition-all" style={{ width: `${next_milestone.progress_pct}%` }} />
            </div>
          </section>
        )}

        <section className="card-tactile rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2"><Heart className="h-4 w-4 text-[color:var(--accent-ink)]" /> Send a cheer</h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">Tap a reaction on any dose. They'll get a soft nudge from you.</p>
          </div>
          <Input placeholder="Your name (Mom, Dad, Alex…)" value={name} onChange={(e) => setName(e.target.value)} data-testid="caregiver-name" className="sm:max-w-xs" />
        </section>

        <section className="card-tactile rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="caregiver-push-section">
          <div className="h-10 w-10 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
            {cgSubscribed ? <Bell className="h-5 w-5 text-[color:var(--primary-ink)]" /> : <BellOff className="h-5 w-5 text-[color:var(--text-muted)]" />}
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg tracking-tight">Get reminders on your device</h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">You'll also get a push when a dose is coming up — helpful when you're the one reminding them.</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={cgSubscribed} onCheckedChange={toggleCaregiverPush} disabled={cgBusy} data-testid="caregiver-push-toggle" />
            <span className="text-xs text-[color:var(--text-muted)]">{cgSubscribed ? "On" : "Off"}</span>
          </div>
        </section>

        <DigestSubscribeCard token={token} />

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-tactile rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg tracking-tight mb-3">Recent</h3>
            {recent.length === 0 ? <p className="text-sm text-[color:var(--text-muted)]">Nothing yet.</p> :
              <ul className="divide-y divide-[color:var(--border-subtle)]">
                {recent.map(d => (
                  <li key={d.id} className="py-3" data-testid={`share-dose-${d.id}`}>
                    <div className="flex items-center gap-3 text-sm">
                      {statusIcon(d.status)}
                      <span className="font-semibold flex items-center gap-1">
                        {fmtDay(d.scheduled_at)} · {fmtTime(d.scheduled_at)}
                        {d.pre_setup && <span title="Before course setup" className="text-[color:var(--text-muted)]">*</span>}
                      </span>
                      <span className={`dose-pill ml-auto ${d.status === "taken" || d.status === "auto_taken" ? "taken" : d.status === "skipped" ? "skipped" : "pending"}`}>
                        {d.status === "auto_taken" ? "taken*" : d.status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {REACTIONS.map(r => (
                        <button
                          key={r.emoji}
                          onClick={() => react(d.id, r.emoji)}
                          disabled={sending[d.id + r.emoji]}
                          data-testid={`react-${d.id}-${r.emoji}`}
                          className="px-2 h-8 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface)] hover:bg-[color:var(--surface-2)] text-sm font-semibold inline-flex items-center gap-1"
                          title={r.label}
                        >
                          <span className="text-base leading-none">{r.emoji}</span>
                        </button>
                      ))}
                      {d.reaction_count > 0 && (
                        <span className="text-xs text-[color:var(--text-muted)] ml-1">
                          {d.reactions?.slice(-5).map(rr => rr.emoji).join("")} · {d.reaction_count}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>}
          </div>
          <div className="card-tactile rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg tracking-tight mb-3">Upcoming</h3>
            {upcoming.length === 0 ? <p className="text-sm text-[color:var(--text-muted)]">Course complete.</p> :
              <ul className="divide-y divide-[color:var(--border-subtle)]">
                {upcoming.map(d => (
                  <li key={d.id} className="py-2 flex items-center gap-3 text-sm">
                    {statusIcon(d.status)}
                    <span className="font-semibold">{fmtDay(d.scheduled_at)} · {fmtTime(d.scheduled_at)}</span>
                    <span className="text-[color:var(--text-muted)] ml-auto">{d.dose_label || rx.dosage}</span>
                  </li>
                ))}
              </ul>}
          </div>
        </section>

        {recent_reactions.length > 0 && (
          <section className="card-tactile rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg tracking-tight mb-3">Recent cheers</h3>
            <ul className="space-y-1 text-sm">
              {recent_reactions.slice(0, 10).map(r => (
                <li key={r.id} className="flex items-center gap-2">
                  <span className="text-base">{r.emoji}</span>
                  <span className="font-semibold">{r.from_name}</span>
                  <span className="text-[color:var(--text-muted)]">· {new Date(r.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {events.length > 0 && (
          <section className="card-tactile rounded-2xl p-6" data-testid="events-feed">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg tracking-tight">Activity feed</h3>
              <a
                href={`${API}/share/${token}/activity.csv`}
                download
                data-testid="share-csv-download"
                className="text-sm text-[color:var(--primary-ink)] font-semibold inline-flex items-center gap-1 hover:underline"
              >
                <FileDown className="h-4 w-4" /> Download CSV
              </a>
            </div>
            <ul className="divide-y divide-[color:var(--border-subtle)]">
              {events.slice(0, 20).map((e, i) => {
                let icon, txt;
                if (e.type === "taken") { icon = <CheckCircle2 className="h-4 w-4 text-[color:var(--success)]" />; txt = <>Dose taken <span className="text-[color:var(--text-muted)]">· scheduled for {fmtTime(e.dose_at)}</span></>; }
                else if (e.type === "skipped") { icon = <XCircle className="h-4 w-4 text-[color:var(--accent-ink)]" />; txt = <>Dose skipped <span className="text-[color:var(--text-muted)]">· scheduled for {fmtTime(e.dose_at)}</span></>; }
                else { icon = <span className="text-base leading-none">{e.emoji}</span>; txt = <><span className="font-semibold">{e.from_name}</span> sent a cheer</>; }
                return (
                  <li key={`${e.type}-${i}-${e.at}`} className="py-2 flex items-center gap-3 text-sm">
                    <span className="w-5 flex items-center justify-center">{icon}</span>
                    <span className="flex-1 min-w-0">{txt}</span>
                    <span className="text-xs text-[color:var(--text-muted)]">{new Date(e.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className="card-tactile rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg tracking-tight">Want your own?</h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">Handy is a free multi-utility toolkit — prescriptions, to-do, shopping, notes, and more.</p>
          </div>
          <a href="/" data-testid="share-cta" className="inline-flex items-center gap-2 px-4 h-11 rounded-lg bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-semibold text-sm">
            Open Handy <ArrowUpRight className="h-4 w-4" />
          </a>
        </section>

        <footer className="text-xs text-[color:var(--text-muted)] pt-2">
          This page is read-only. The person managing this prescription controls the schedule.
        </footer>
      </main>
    </div>
  );
}
